/**
 * Fast-scroll gap-skeleton trimming, shared by BOTH results grids (CLAUDE.md
 * § "Two Results Grids — Always Check Parity": ResultsGrid + TableExplorerGrid).
 *
 * While a fast scroll leaves the deferred row window with no overlap against
 * the live viewport, the grids paint skeleton rows at the live position. The
 * skeleton window starts as the OVERSCANNED virtualization window; this trim
 * drops up to `overscan` rows from each side (skeletons exist only for the
 * frame or two the scroll is in flight — off-screen ones just make the cheap
 * urgent commit less cheap) and moves the trimmed height into the paddings,
 * keeping the scroll geometry exact:
 *
 *   topPaddingHeight + count * rowHeight + bottomPaddingHeight
 *     === original top + (endIndex - startIndex) * rowHeight + original bottom
 *
 * That invariant holds for BOTH padding conventions: the pixel-mapped mode
 * (padding = rowIndex * rowHeight) and ResultsGrid's proportional mode, where
 * paddings are ratio-derived — the trim only ever moves whole rows between
 * the skeleton run and the adjacent padding.
 */

export type GapSkeletonSourceWindow = {
  startIndex: number;
  endIndex: number;
  topPaddingHeight: number;
  bottomPaddingHeight: number;
};

export type GapSkeletonWindow = {
  startIndex: number;
  count: number;
  topPaddingHeight: number;
  bottomPaddingHeight: number;
};

export function trimGapSkeletonWindow(
  window: GapSkeletonSourceWindow,
  effectiveHeight: number,
  rowHeight: number,
  overscan: number,
): GapSkeletonWindow {
  const { startIndex, endIndex, topPaddingHeight, bottomPaddingHeight } = window;
  const visibleCount = Math.ceil(effectiveHeight / rowHeight) + 2;
  const total = endIndex - startIndex;
  const spare = Math.max(0, total - visibleCount);
  const trimTop = Math.min(overscan, Math.floor(spare / 2));
  const trimBottom = Math.min(overscan, spare - trimTop);
  return {
    startIndex: startIndex + trimTop,
    count: total - trimTop - trimBottom,
    topPaddingHeight: topPaddingHeight + trimTop * rowHeight,
    bottomPaddingHeight: bottomPaddingHeight + trimBottom * rowHeight,
  };
}
